<template>
    <div>
        <ts-page-title
            :title="$t('moveLoanWriteOff.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('moveLoanWriteOff.pageTitle'), active: true }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper class="tw-flex tw-items-center tw-justify-between">
                <Button type="info" ghost @click="addNew">{{ $t('addNew') }}</Button>
                <div class="tw-flex tw-items-center tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('moveLoanWriteOff.search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        enter-button
                        @on-search="onSearch"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                    <ts-button
                        v-tooltip="$t('filter')"
                        @click.prevent="visible = true"
                    >
                        <i class="fa fa-filter"></i>
                    </ts-button>
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
            >
                <template slot-scope="{ row }" slot="moved_at">
                    {{ row.moved_at ? row.moved_at.substring(0, 19).replace('T', ' ') : '' }}
                </template>
                <template slot-scope="{ row }" slot="loan">
                    <div><code class="tw-text-xs">{{ row.loan_number || '—' }}</code></div>
                    <div class="tw-text-xs tw-text-gray-500">
                        {{ row.customer_name_en || row.customer_name_kh || '—' }}
                    </div>
                </template>
                <template slot-scope="{ row }" slot="from">
                    <div><strong>{{ row.old_branch_code || '—' }}</strong></div>
                    <div class="tw-text-xs tw-text-gray-500">{{ row.old_co_name_en || '—' }}</div>
                </template>
                <template slot-scope="{ row }" slot="to">
                    <div><strong class="tw-text-green-600">{{ row.new_branch_code || '—' }}</strong></div>
                    <div class="tw-text-xs tw-text-gray-500">{{ row.new_co_name_en || '—' }}</div>
                </template>
                <template slot-scope="{ row }" slot="moved_by">
                    {{
                        row.moved_by
                            ? (row.moved_by.employee
                                ? row.moved_by.employee.employee_name_en
                                : row.moved_by.user_name)
                            : ''
                    }}
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination v-model="pagination" @navigate="fetchData" />
            </div>

            <Drawer
                :title="$t('filter')"
                :closable="false"
                :value="visible"
                @on-close="() => (visible = false)"
                width="340px"
            >
                <div class="mb-3">
                    <label class="form-col-label control-label">From Branch</label>
                    <Select
                        v-model="filters.from_branch_id"
                        filterable
                        clearable
                        placeholder="Any branch"
                        @on-change="onFilterChange"
                    >
                        <Option
                            v-for="b in branches"
                            :value="b.branch_id"
                            :key="b.branch_id"
                            :label="b.branch_code + ' | ' + (b.branch_name_kh || '')"
                        >
                            {{ b.branch_code }} | {{ b.branch_name_kh || '' }}
                        </Option>
                    </Select>
                </div>
                <div class="mb-3">
                    <label class="form-col-label control-label">To Branch</label>
                    <Select
                        v-model="filters.to_branch_id"
                        filterable
                        clearable
                        placeholder="Any branch"
                        @on-change="onFilterChange"
                    >
                        <Option
                            v-for="b in branches"
                            :value="b.branch_id"
                            :key="b.branch_id"
                            :label="b.branch_code + ' | ' + (b.branch_name_kh || '')"
                        >
                            {{ b.branch_code }} | {{ b.branch_name_kh || '' }}
                        </Option>
                    </Select>
                </div>
                <div class="mb-3">
                    <label class="form-col-label control-label">Loan Number</label>
                    <Input
                        v-model="filters.loan_number"
                        placeholder="e.g. BTB230116001"
                        clearable
                        @on-change="onFilterChange"
                    />
                </div>
                <div class="mb-3">
                    <label class="form-col-label control-label">Customer</label>
                    <Input
                        v-model="filters.customer"
                        placeholder="customer name (EN or KH)"
                        clearable
                        @on-change="onFilterChange"
                    />
                </div>
                <div class="mb-3">
                    <label class="form-col-label control-label">Credit Officer</label>
                    <Input
                        v-model="filters.co"
                        placeholder="CO name — matches from OR to CO"
                        clearable
                        @on-change="onFilterChange"
                    />
                </div>
                <div class="tw-flex tw-justify-end tw-space-x-2 tw-mt-4">
                    <ts-button class="btn-gray" @click.prevent="resetFilters">Reset</ts-button>
                </div>
            </Drawer>

            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                :title="$t('moveLoanWriteOff.pageTitle')"
                width="720"
            >
                <from-action
                    ref="form_action"
                    @cancel="clearEdit"
                    @fetchData="fetchData"
                />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import FromAction from './form'

export default {
    name: 'moveLoanWriteOffIndex',
    components: { FromAction },
    data () {
        return {
            loading: false,
            showForm: false,
            visible: false,
            width: 'width: 300px',
            filters: {
                from_branch_id: null,
                to_branch_id: null,
                loan_number: '',
                customer: '',
                co: ''
            }
        }
    },
    computed: {
        ...mapState('creditOperation/moveLoanWriteOff', ['resources', 'pagination', 'branches']),
        search: {
            get () { return this.$store.state.creditOperation.moveLoanWriteOff.search },
            set (v) {
                this.$store.commit('creditOperation/moveLoanWriteOff/SET_SEARCH', v)
                this.$store.commit('creditOperation/moveLoanWriteOff/RESET_CURRENT_PAGE')
            }
        },
        columns () {
            return [
                { title: this.$t('moveLoanWriteOff.movedAt'), slot: 'moved_at', width: 160 },
                { title: this.$t('moveLoanWriteOff.loan'),    slot: 'loan',     minWidth: 220 },
                { title: this.$t('moveLoanWriteOff.from'),    slot: 'from',     minWidth: 180 },
                { title: this.$t('moveLoanWriteOff.to'),      slot: 'to',       minWidth: 180 },
                { title: this.$t('moveLoanWriteOff.reason'),  key: 'reason',    minWidth: 200 },
                { title: this.$t('moveLoanWriteOff.movedBy'), slot: 'moved_by', width: 140 }
            ]
        }
    },
    methods: {
        ...mapActions('creditOperation/moveLoanWriteOff', ['getBranches']),
        onSearch () { this.fetchData() },
        fetchData (attributes) {
            this.loading = true
            // Fold current filters into every fetch so pagination/sort re-use them.
            const payload = Object.assign({}, this.filters, attributes)
            this.$store
                .dispatch('creditOperation/moveLoanWriteOff/fetch', payload)
                .then(() => { this.loading = false })
                .catch(error => {
                    this.loading = false
                    this.notice({ type: 'error', text: error.message })
                })
        },
        onFilterChange: debounce(function () {
            this.$store.commit('creditOperation/moveLoanWriteOff/RESET_CURRENT_PAGE')
            this.fetchData()
        }, 400),
        resetFilters () {
            this.filters.from_branch_id = null
            this.filters.to_branch_id = null
            this.filters.loan_number = ''
            this.filters.customer = ''
            this.filters.co = ''
            this.$store.commit('creditOperation/moveLoanWriteOff/RESET_CURRENT_PAGE')
            this.fetchData()
        },
        addNew () {
            this.showForm = true
            this.$refs.form_action.openForm()
        },
        clearEdit () {
            this.showForm = false
            this.$refs.form_action.clearInput()
        },
        notice (not) {
            this.$Notice[not.type]({ title: 'MOVE LOAN WRITE-OFF', desc: not.text })
        }
    },
    beforeRouteEnter (to, from, next) {
        next(vm => {
            vm.fetchData()
            vm.getBranches()
        })
    },
    beforeRouteLeave (to, from, next) {
        this.$store.commit('creditOperation/moveLoanWriteOff/RESET_STATE')
        next()
    }
}
</script>
